import { Box, Typography, Tooltip, CircularProgress } from '@mui/material'
import { TableColumn } from 'types/types'
import dayjs from 'dayjs'
import { getFirstWords } from 'utils'
import * as sxStyles from './columns.styled'
import {
  REQUESTSTATUS,
  TRecalculateTask,
} from 'store/slices/recalculateBinSlice'

export const incomingDeliveriesTableColumns = (
  wheatCodeOptions?: { value: string; label: string }[]
): TableColumn[] => [
  {
    id: 'date',
    label: 'Date',
    minWidth: 150,
    filterParams: {
      filterVariant: 'date',
    },
    sortable: true,
    format: (_value, row) => (
      <span>{dayjs(row?.nir?.arrival_date).format('DD/MM/YYYY')}</span>
    ),
  },
  {
    id: 'time',
    label: 'Time',
    minWidth: 80,
    format: (_value, row) => (
      <span>{dayjs(row?.nir?.arrival_date).format('HH:mmA')}</span>
    ),
  },
  {
    id: 'name',
    label: 'Name',
    minWidth: 300,
    align: 'left',
    filterParams: {
      filterVariant: 'string',
    },
    sortable: true,
    format: (value) => (
      <Box sx={sxStyles.nameBoxStyle}>
        <Tooltip title={value}>
          <Typography sx={sxStyles.nameTooltipStyle}>
            {getFirstWords(value, 5)}
          </Typography>
        </Tooltip>
      </Box>
    ),
  },
  {
    id: 'wheatCode',
    label: 'Wheat Code',
    filterParams: {
      filterVariant: 'select',
      filterField: 'wheat_code_id',
      filterOptions: wheatCodeOptions,
    },
    minWidth: 140,
    format: (_value, row) => (
      <Typography sx={sxStyles.wheatCodeTypographyStyle}>
        {row?.wheat_code?.code}
      </Typography>
    ),
  },
]

export const storedDeliveriesTableColumns = (
  wheatCodeWithTaskIds: TRecalculateTask[] | null,
  showDiscarded: boolean,
  showStorage: boolean
): TableColumn[] => {
  const getAmountNumerator = (row: any) => {
    const accepted = row?.accepted_released_delivery_count ?? 0
    const discarded = row?.discarded_deliveries_count ?? 0
    const storage = row?.storage_deliveries_count ?? 0

    let numerator = accepted

    if (showDiscarded) {
      numerator += discarded
    }

    if (showStorage) {
      numerator += storage
    }

    return numerator
  }

  const getAmountTooltip = () => {
    if (showDiscarded && showStorage) {
      return 'Accepted + Discarded + Storage Delivery Count / Total Delivery Count'
    }
    if (showDiscarded) {
      return 'Accepted + Discarded Delivery Count / Total Delivery Count'
    }
    if (showStorage) {
      return 'Accepted + Storage Delivery Count / Total Delivery Count'
    }
    return 'Accepted Delivery Count / Total Delivery Count'
  }

  const columns: TableColumn[] = [
    {
      id: 'code',
      label: 'Code',
      minWidth: 190,
      align: 'left',
      format: (value) => {
        return <Typography>{`Wheat Code: ${value}`}</Typography>
      },
    },
    {
      id: 'amount',
      label: 'Amount',
      minWidth: 100,
      align: 'left',
      format: (_value, row) => {
        const numerator = getAmountNumerator(row)
        const denominator = row?.total_deliveries_count ?? 0
        return (
          <Tooltip title={getAmountTooltip()}>
            <Typography>{`${numerator}/${denominator}`}</Typography>
          </Tooltip>
        )
      },
    },
    {
      id: 'processing',
      label: 'Processing',
      minWidth: 100,
      align: 'right',
      format: (_value, row) => {
        const task = wheatCodeWithTaskIds?.find(
          (item) =>
            item.wheatCodeId === row.id &&
            item.status === REQUESTSTATUS.IN_PROGRESS
        )
        return task ? <CircularProgress color="primary" size="1.5rem" /> : null
      },
    },
  ]
  return columns
}
